import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Form,
  Modal,
  Card,
  ListGroup,
  Row,
  Col,
  Alert,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const LeagueTableManagement = () => {
  const [seasons, setSeasons] = useState([]);
  const [teamLevels, setTeamLevels] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [tables, setTables] = useState([]);
  const [unassignedTeams, setUnassignedTeams] = useState([]);

  // State for modals
  const [showCreateTableModal, setShowCreateTableModal] = useState(false);
  const [newTableName, setNewTableName] = useState("");
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [teamToAdd, setTeamToAdd] = useState("");

  // Fetch initial data for dropdowns
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [seasonsRes, levelsRes] = await Promise.all([
          axios.get(`${API_URL}/seasons`),
          axios.get(`${API_URL}/team-levels`),
        ]);
        setSeasons(seasonsRes.data);
        setTeamLevels(levelsRes.data);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch tables and unassigned teams when selections change
  useEffect(() => {
    if (selectedSeason && selectedLevel) {
      fetchTablesAndTeams();
    } else {
      setTables([]); // Clear tables if selection is incomplete
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason, selectedLevel]);

  const fetchTablesAndTeams = async () => {
    try {
      const [tablesRes, unassignedRes] = await Promise.all([
        axios.get(
          `${API_URL}/details/tables?season_id=${selectedSeason}&level_team_id=${selectedLevel}`,
        ),
        axios.get(
          `${API_URL}/details/unassigned-teams?season_id=${selectedSeason}&level_team_id=${selectedLevel}`,
        ),
      ]);
      setTables(tablesRes.data);
      setUnassignedTeams(unassignedRes.data);
    } catch (error) {
      console.error("Error fetching tables and teams:", error);
    }
  };

  const handleCreateTable = async () => {
    if (!newTableName) return;
    try {
      await axios.post(`${API_URL}/details/tables`, {
        name: newTableName,
        season_id: selectedSeason,
        level_team_id: selectedLevel,
      });
      setShowCreateTableModal(false);
      setNewTableName("");
      fetchTablesAndTeams();
    } catch (error) {
      console.error("Error creating table:", error);
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (
      window.confirm(
        "Bạn có chắc chắn muốn xóa bảng này? Mọi đội trong bảng sẽ bị xóa theo.",
      )
    ) {
      await axios.delete(`${API_URL}/details/tables/${tableId}`);
      fetchTablesAndTeams();
    }
  };

  const handleAddTeam = async () => {
    if (!teamToAdd) return;
    try {
      await axios.post(`${API_URL}/details/season-tables`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        table_id: selectedTableId,
        team_id: teamToAdd,
      });
      setShowAddTeamModal(false);
      setTeamToAdd("");
      fetchTablesAndTeams();
    } catch (error) {
      console.error("Error adding team to table:", error);
    }
  };

  const handleRemoveTeam = async (tableId, teamId) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa đội này khỏi bảng?")) {
      await axios.delete(`${API_URL}/details/season-tables`, {
        data: { table_id: tableId, team_id: teamId },
      });
      fetchTablesAndTeams();
    }
  };

  return (
    <div>
      <h2>Quản lý Bảng đấu</h2>
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Chọn Mùa giải</Form.Label>
            <Form.Select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">-- Chọn Mùa giải --</option>
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Chọn Hạng Đội</Form.Label>
            <Form.Select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
            >
              <option value="">-- Chọn Hạng Đội --</option>
              {teamLevels.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {selectedSeason && selectedLevel && (
        <div>
          <Button
            className="mb-4"
            onClick={() => setShowCreateTableModal(true)}
          >
            Tạo Bảng mới
          </Button>
          <Row>
            {tables.map((table) => (
              <Col md={6} key={table.id} className="mb-4">
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    {table.name}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteTable(table.id)}
                    >
                      Xóa Bảng
                    </Button>
                  </Card.Header>
                  <ListGroup variant="flush">
                    {table.teams.map((team) => (
                      <ListGroup.Item
                        key={team.id}
                        className="d-flex justify-content-between"
                      >
                        {team.name}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveTeam(table.id, team.id)}
                        >
                          Xóa
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  <Card.Body>
                    <Button
                      variant="success"
                      onClick={() => {
                        setSelectedTableId(table.id);
                        setShowAddTeamModal(true);
                      }}
                    >
                      Thêm Đội
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Create Table Modal */}
      <Modal
        show={showCreateTableModal}
        onHide={() => setShowCreateTableModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Tạo Bảng mới</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            placeholder="Nhập tên bảng (vd: Bảng A)"
            value={newTableName}
            onChange={(e) => setNewTableName(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateTableModal(false)}
          >
            Hủy
          </Button>
          <Button variant="primary" onClick={handleCreateTable}>
            Tạo
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Team Modal */}
      <Modal show={showAddTeamModal} onHide={() => setShowAddTeamModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Thêm Đội vào Bảng</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {unassignedTeams.length > 0 ? (
            <Form.Select onChange={(e) => setTeamToAdd(e.target.value)}>
              <option value="">-- Chọn Đội --</option>
              {unassignedTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
          ) : (
            <Alert variant="warning">Không còn đội nào để thêm vào bảng.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddTeamModal(false)}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleAddTeam}
            disabled={unassignedTeams.length === 0}
          >
            Thêm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeagueTableManagement;
